import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useSettings } from '@/api/settings';
import { Card, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card';
import { DollarSign, CalendarCheck2, CalendarX, FileClock, Users, ShieldCheck, FileText, Banknote, MapPin, CalendarRange, Target, UserSearch, FileX2 } from 'lucide-react';

/**
 * Reporting hub. Each card is a distinct report built in Phase 2.6. The hub
 * is the durable entry point so additional reports added later drop in with
 * a new ReportLink entry rather than changing anything in the router.
 *
 * Permission gating: the hub itself requires view_reports (sidebar enforces
 * this). Export-only roles still see the hub — the per-report export button
 * is gated independently on export_reports.
 */

interface ReportLink {
    title: string;
    subtitle: string;
    to: string;
    icon: React.ComponentType<{ className?: string }>;
    ready: boolean;
    requires: string;
    /** Hidden unless a practice-level toggle is on. EVV is the only one today. */
    settingGate?: 'evvEnabled';
}

const REPORTS: ReportLink[] = [
    {
        title: 'Billing & AR',
        subtitle: 'Collections, aging, payer mix, patient-responsibility outstanding',
        to: '/reporting/billing-ar',
        icon: DollarSign,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Session Activity',
        subtitle: 'Scheduled, completed, canceled by client / staff / reason',
        to: '/reporting/session-activity',
        icon: CalendarCheck2,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Cancellation Rollup',
        subtitle: 'Cancellations grouped by reason × payer × month',
        to: '/reporting/cancellation-rollup',
        icon: CalendarX,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Authorization Utilization',
        subtitle: 'Hours used vs. authorized; flag expiring and high-utilization auths',
        to: '/reporting/auth-utilization',
        icon: FileClock,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Payroll',
        subtitle: 'Payable hours per staff over a date range',
        to: '/reporting/payroll',
        icon: Banknote,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Availability Overlap',
        subtitle: 'Weekly view of a staff member\u2019s and client\u2019s availability with existing commitments',
        to: '/reporting/availability-overlap',
        icon: CalendarRange,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Staff Performance',
        subtitle: 'Completion %, cancellation rate, note-completion rate per staff',
        to: '/reporting/staff-performance',
        icon: Users,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Supervision Compliance',
        subtitle: 'BACB ratio (≥5%) + ≥1 Individual contact per month, per RBT',
        to: '/reporting/supervision',
        icon: ShieldCheck,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Claim Denial Analysis',
        subtitle: 'Denial reason \u00D7 payer \u00D7 CPT rollup so re-work targets the highest-impact combos',
        to: '/reporting/claim-denials',
        icon: FileX2,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Client PHI Access',
        subtitle: 'Disclosure-accounting list for one client \u2014 who looked at this chart, when, and what for (\u00A7164.528)',
        to: '/reporting/client-phi-access',
        icon: UserSearch,
        ready: true,
        requires: 'view_audit_log',
    },
    {
        title: 'Goal Mastery',
        subtitle: 'Per-client treatment-plan progress; snapshot + cohort metrics for reauth packets and parent updates',
        to: '/reporting/goal-mastery',
        icon: Target,
        ready: true,
        requires: 'view_reports',
    },
    {
        title: 'Session Notes Export',
        subtitle: 'Word .docx of one client\u2019s session notes across a date window',
        to: '/reporting/session-notes',
        icon: FileText,
        ready: true,
        requires: 'export_reports',
    },
    {
        title: 'Visit Verification',
        subtitle: 'Check-in / check-out log with GPS match and status — for internal audit and billing defense',
        to: '/reporting/evv',
        icon: MapPin,
        ready: true,
        requires: 'view_reports',
        settingGate: 'evvEnabled',
    },
];

export function ReportingPage() {
    const hasPermission = useAuthStore((s) => s.hasPermission);
    const settings = useSettings();
    const evvEnabled = !!settings.data?.scheduling?.evvEnabled;

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="mb-6">
                <h1 className="font-display text-2xl font-bold text-text">Reporting</h1>
                <p className="text-xs text-text-subtle mt-1">
                    Aggregate views across clinical, scheduling, and billing data. Exports stream from the server with an audit entry on every download.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {REPORTS
                    .filter((r) => hasPermission(r.requires))
                    .filter((r) => r.settingGate !== 'evvEnabled' || evvEnabled)
                    .map((r) => {
                    const Icon = r.icon;
                    const content = (
                        <Card
                            className={
                                r.ready
                                    ? 'transition-colors hover:border-accent cursor-pointer h-full'
                                    : 'opacity-60 h-full'
                            }
                        >
                            <CardHeader>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-md bg-surface-muted text-accent-text">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1">
                                        <CardTitle>{r.title}</CardTitle>
                                        <CardSubtitle>{r.subtitle}</CardSubtitle>
                                    </div>
                                    {!r.ready && (
                                        <span className="text-2xs uppercase tracking-wider text-text-subtle">
                                            Coming soon
                                        </span>
                                    )}
                                </div>
                            </CardHeader>
                        </Card>
                    );
                    return r.ready ? (
                        <Link key={r.to} to={r.to} className="block">
                            {content}
                        </Link>
                    ) : (
                        <div key={r.to}>{content}</div>
                    );
                })}
            </div>
        </div>
    );
}
